import { Component, Element, h, Host, Prop, Event, EventEmitter, Watch } from '@stencil/core';
import { ImageAlignment } from './types';

export interface AhaVisualSelection extends HTMLAhaVisualSelectionElement {}

/**
 * @slot description - Replace the description of the selection-item with custom
 *                     markup. You can have rich content in this slot. See
 *                     example below.
 * @slot title - Replace the title portion of the selection-item with
 *               custom markup. You can have rich content here like labels or
 *               tooltips. See examples below.
 * @slot icon - Add an icon to the right side of the selection component. This
 *              is often used then the selection item is behaving more like a
 *              link or button and not a radio-button.
 */
@Component({
  tag: 'aha-visual-selection',
  styleUrl: 'aha-visual-selection.css',
  shadow: true,
})
export class AhaVisualSelection {
  /**
   * Image source. If you are using multiple images, this is the one that will
   * be used when the selection is selected or hovered.
   *
   * If a single source is used, this image will have a grayscale applied when
   * the selection is not selected or
   */
  @Prop({ reflect: true }) src?: string;

  /**
   * Source for an image when it is not hovered or selected.
   *
   * When this image is supplied, it is used and no filtering is applied to the
   * src image.
   */
  @Prop({ reflect: true }) notSelectedSrc?: string;

  /**
   * controls the way that the image is aligned. It can be aligned 'top' or 'center'
   */
  @Prop({ reflect: true, attribute: 'align-image' }) alignImage: ImageAlignment = 'center';

  /**
   * controls the way that the card items are aligned. If true, the image and text will be vertically aligned
   */
  @Prop({ reflect: true }) verticallyAligned?: boolean;

  /**
   * Alt text for image
   */
  @Prop({ reflect: true }) alt?: string;

  /**
   * True if item is selected, false otherwise
   */
  @Prop({ reflect: true }) selected?: boolean;
  @Watch('selected')
  selectedChanged(value: boolean) {
    if (value) this.selectEvent.emit({ element: this.host });
  }

  @Event({
    eventName: 'aha-visual-selection:select',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  selectEvent!: EventEmitter;

  @Element() host!: HTMLAhaVisualSelectionElement;

  render() {
    return (
      <Host
        class={
          this.verticallyAligned ? 'visual-selection__vertical' : 'visual-selection__horizontal'
        }
      >
        <div class="visual-selection__image-container">
          {!this.notSelectedSrc ? (
            <img class="image" alt={this.alt} src={this.src} />
          ) : (
            <div class="multiple-images">
              <img class="image image--not-selected" alt={this.alt} src={this.notSelectedSrc} />
              <img class="image image--selected" alt={this.alt} src={this.src} />
            </div>
          )}
        </div>
        <div class="visual-selection__description">
          <span class="visual-selection__description-title">
            <slot name="title"></slot>
          </span>
          <div class="visual-selection__description-text">
            <slot name="description"></slot>
          </div>
        </div>
        <div class="visual-selection__icon">
          <slot name="icon"></slot>
        </div>
      </Host>
    );
  }
}
